import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getOrders, getOrder, cancelOrder, updateProfile, updatePassword, confirmDelivery } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import TopPromoBar from '../components/TopPromoBar';
import Header from '../components/Header';
import logo from '../logo.png';
import config from '../config';

// Awaiting Payment Banner with Countdown Timer
function AwaitingPaymentBanner({ order }) {
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (!order || !order.created_at) return;

    const calculateTimeRemaining = () => {
      const orderCreatedAt = new Date(order.created_at);
      const now = new Date();
      const elapsedSeconds = Math.floor((now - orderCreatedAt) / 1000);
      const totalTimeoutSeconds = 10 * 60; // 10 minutes
      const remaining = Math.max(0, totalTimeoutSeconds - elapsedSeconds);
      
      return remaining;
    };

    // Calculate initial time
    setTimeRemaining(calculateTimeRemaining());

    // Update every second
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      
      // Stop updating if countdown reaches 0
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [order]);

  // Format seconds as MM:SS
  const formatTime = (seconds) => {
    if (seconds === null || seconds <= 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-bold border-2 border-yellow-500 bg-yellow-50 text-yellow-700 w-fit">
      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span>Awaiting Payment</span>
      {timeRemaining !== null && timeRemaining > 0 && (
        <span className="ml-1 font-mono text-xs font-bold text-yellow-800">
          ({formatTime(timeRemaining)})
        </span>
      )}
    </span>
  );
}

function UserOrders({ user, onUserUpdate, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const cartButtonRef = useRef(null);
  const { app } = config;
  const APP_NAME = app.name;
  // Removed account tab - account settings moved to Profile page
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderDetailOpen, setIsOrderDetailOpen] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);
  const isInitialLoad = useRef(true);
  const previousStatusFilter = useRef(null);
  
  // Account form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });
  const [shippingForm, setShippingForm] = useState({
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_postal_code: '',
    shipping_country: '',
  });
  const [billingForm, setBillingForm] = useState({
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: '',
  });
  const [paymentForm, setPaymentForm] = useState({
    payment_method: '',
    card_last_four: '',
    card_brand: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [useShippingForBilling, setUseShippingForBilling] = useState(false);
  const [useBillingForShipping, setUseBillingForShipping] = useState(false);

  useEffect(() => {
    // Don't redirect here - ProtectedRoute handles authentication
    // Just initialize forms when user data is available
    if (!user) {
      return;
    }
    
    // Check for order success from checkout (only show once per order)
    if (location.state?.orderSuccess && location.state?.orderNumber) {
      const orderNumber = location.state.orderNumber;
      const shownKey = `orderSuccess_${orderNumber}`;
      
      // Check if we've already shown this notification (persists across refreshes)
      if (!sessionStorage.getItem(shownKey)) {
        if (location.state?.pendingPayment) {
          showSuccess(`Order ${orderNumber} created! Payment prompt sent. Please approve on your phone.`);
        } else {
          showSuccess(`Order ${orderNumber} placed successfully!`);
        }
        sessionStorage.setItem(shownKey, 'true');
        // Clear the location state to prevent showing on refresh
        navigate(location.pathname, { replace: true, state: {} });
        // Refresh orders list
        fetchOrders();
      } else {
        // Already shown - just clear the stale state
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
    
    // Initialize profile form with user data
    setProfileForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address || '',
    });
    
    // Initialize shipping form
    setShippingForm({
      shipping_address: user.shipping_address || '',
      shipping_city: user.shipping_city || '',
      shipping_state: user.shipping_state || '',
      shipping_postal_code: user.shipping_postal_code || '',
      shipping_country: user.shipping_country || '',
    });
    
    // Initialize billing form
    setBillingForm({
      billing_address: user.billing_address || '',
      billing_city: user.billing_city || '',
      billing_state: user.billing_state || '',
      billing_postal_code: user.billing_postal_code || '',
      billing_country: user.billing_country || '',
    });
    
    // Initialize payment form
    setPaymentForm({
      payment_method: user.payment_method || '',
      card_last_four: user.card_last_four || '',
      card_brand: user.card_brand || '',
    });
    
    // Suppress notifications only when statusFilter changes (user clicked a filter button)
    // Show notifications on initial load or when other dependencies change
    const isStatusFilterChange = previousStatusFilter.current !== null && previousStatusFilter.current !== statusFilter;
    const suppressNotifications = isStatusFilterChange;
    
    fetchOrders(suppressNotifications);
    
    // Update the previous statusFilter ref
    previousStatusFilter.current = statusFilter;
    
    // Mark that initial load is complete
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    }
    
    // Start polling for pending payments
    let pollCleanup = null;
    const pendingOrderId = sessionStorage.getItem('pending_order_id');
    if (pendingOrderId && location.state?.pendingPayment) {
      pollCleanup = startPaymentPolling(parseInt(pendingOrderId, 10));
    }
    
    // Auto-refresh orders periodically so status/counts and payment status stay in sync with the API
    const REFRESH_INTERVAL_MS = 30000; // 30 seconds
    const refreshIntervalId = setInterval(() => {
      fetchOrders(true, true); // suppress notifications, background (no loading spinner)
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (pollCleanup) {
        pollCleanup();
      }
      clearInterval(refreshIntervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, statusFilter, navigate, location.state]);

  // When any order is awaiting payment, poll more often so status flips to "Payment successful" quickly
  useEffect(() => {
    const hasAwaitingPayment = orders.some(
      o => o.payment_status === 'pending_payment' || o.payment_status === 'Pending Payment'
    );
    if (!hasAwaitingPayment) return;
    const PAYMENT_POLL_MS = 10000; // 10 seconds
    const id = setInterval(() => fetchOrders(true, true), PAYMENT_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const fetchOrders = async (suppressNotifications = false, background = false) => {
    try {
      if (!background) {
        setLoading(true);
      }
      const token = localStorage.getItem('auth_token');
      if (!token) {
        if (!suppressNotifications) {
          showError('Authentication required. Please log in again.');
        }
        return;
      }
      const data = await getOrders(token, statusFilter);
      if (!suppressNotifications && process.env.NODE_ENV === 'development') {
        console.log('Fetched orders:', data.map(o => ({ 
          order_number: o.order_number, 
          payment_status: o.payment_status,
          status: o.status 
        })));
      }
      setOrders(data);
      // Keep order detail modal in sync so "Awaiting payment" → "Payment successful" updates live
      setSelectedOrder(prev => {
        if (!prev) return prev;
        const updated = data.find(o => o.id === prev.id);
        return updated || prev;
      });
    } catch (error) {
      if (!suppressNotifications) {
        console.error('Failed to fetch orders:', error);
        showError(error.message || 'Failed to load orders. Please try again.');
      } else {
        console.debug('Failed to refresh orders silently:', error);
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  };

  // Poll for payment status updates with progressive intervals
  const startPaymentPolling = (orderId) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;

    let pollCount = 0;
    const maxPolls = 150; // Poll for up to 5 minutes
    let timeoutId = null;
    let isPolling = true;

    // Progressive polling: start faster (2s), then slow down
    const getPollInterval = (count) => {
      if (count <= 30) {
        return 2000; // 2 seconds for first minute (faster initial checks)
      } else if (count <= 60) {
        return 3000; // 3 seconds for next minute
      } else {
        return 4000; // 4 seconds after that (less frequent as time passes)
      }
    };

    const poll = async () => {
      if (!isPolling) return;

      try {
        pollCount++;
        const updatedOrder = await getOrder(orderId, token);
        
        if (updatedOrder) {
          // Update the order in the list
          setOrders(prevOrders => 
            prevOrders.map(order => 
              order.id === orderId ? updatedOrder : order
            )
          );

          // Check payment status
          if (updatedOrder.payment_status === 'paid') {
            isPolling = false;
            if (timeoutId) clearTimeout(timeoutId);
            sessionStorage.removeItem('pending_order_id');
            sessionStorage.removeItem('pending_payment_reference');
            sessionStorage.removeItem('pending_invoice_number');
            sessionStorage.removeItem('pending_payment_method');
            showSuccess('Payment confirmed! Your order is now being processed.');
            // Refresh orders to get latest status
            fetchOrders(true);
            return;
          } else if (updatedOrder.payment_status === 'failed') {
            isPolling = false;
            if (timeoutId) clearTimeout(timeoutId);
            sessionStorage.removeItem('pending_order_id');
            sessionStorage.removeItem('pending_payment_reference');
            sessionStorage.removeItem('pending_invoice_number');
            sessionStorage.removeItem('pending_payment_method');
            showError('Payment failed. Please try again.');
            // Refresh orders to show failed status
            fetchOrders(true);
            return;
          }
          
          // Also check if payment status changed to failed outside of polling
          // This handles cases where payment fails after polling has stopped
          if (updatedOrder.payment_status === 'failed') {
            // Update the order in the list immediately
            setOrders(prevOrders => 
              prevOrders.map(order => 
                order.id === orderId ? { ...order, payment_status: 'failed' } : order
              )
            );
          }
        }

        // Stop polling after max attempts
        if (pollCount >= maxPolls) {
          isPolling = false;
          if (timeoutId) clearTimeout(timeoutId);
          sessionStorage.removeItem('pending_order_id');
          sessionStorage.removeItem('pending_payment_reference');
          sessionStorage.removeItem('pending_invoice_number');
          sessionStorage.removeItem('pending_payment_method');
          console.log('Payment polling stopped after max attempts');
          return;
        }

        // Schedule next poll with progressive interval
        if (isPolling) {
          const interval = getPollInterval(pollCount);
          timeoutId = setTimeout(poll, interval);
        }
      } catch (error) {
        // Silently handle polling errors - don't spam console
        // Network errors are common and expected during temporary connectivity issues
        if (error.message && !error.message.includes('Failed to fetch')) {
          console.debug('Error polling payment status:', error);
        }
        // Continue polling on error - might be temporary network issue
        if (isPolling && pollCount < maxPolls) {
          const interval = getPollInterval(pollCount);
          timeoutId = setTimeout(poll, interval);
        }
      }
    };

    // Start polling immediately
    poll();

    // Return cleanup function
    return () => {
      isPolling = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  };

  const handleViewOrder = async (orderId) => {
    // Find the order from the current list to show immediately (optimistic UI)
    const existingOrder = orders.find(o => o.id === orderId);
    
    // Open modal immediately with existing order data
    if (existingOrder) {
      setSelectedOrder(existingOrder);
      setIsOrderDetailOpen(true);
      setIsLoadingOrderDetails(true);
    }
    
    // Fetch full order details in the background and update the modal
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        setIsLoadingOrderDetails(false);
        return;
      }
      const order = await getOrder(orderId, token);
      // Update the modal with full order details
      setSelectedOrder(order);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      // Only show error if modal is still open
      if (isOrderDetailOpen) {
        showError(error.message || 'Failed to load order details.');
      }
    } finally {
      setIsLoadingOrderDetails(false);
    }
  };

  const handleCancelOrderClick = (orderId) => {
    setOrderToCancel(orderId);
    setIsCancelConfirmOpen(true);
  };

  const handleRetryPayment = (order) => {
    // Navigate to payment page with order details for retry
    const items = Array.isArray(order.orderItems) ? order.orderItems : (Array.isArray(order.order_items) ? order.order_items : []);
    navigate('/payment', {
      state: {
        orderId: order.id,
        order: order,
        selectedItems: items,
        retryPayment: true,
      },
    });
  };

  const handleConfirmDelivery = async (action) => {
    if (action === 'dispute' && !disputeReason.trim()) {
      showError('Please provide a reason for disputing the delivery.');
      return;
    }

    setConfirmingDelivery(true);
    try {
      const token = localStorage.getItem('auth_token');
      await confirmDelivery(selectedOrder.id, action, token, disputeReason, '');
      
      if (action === 'dispute') {
        showSuccess('Delivery dispute submitted. Admin will review your claim.');
        setShowDisputeModal(false);
        setDisputeReason('');
      }
      
      // Refresh order details
      const fullOrder = await getOrder(selectedOrder.id, token);
      setSelectedOrder(fullOrder);
      await fetchOrders();
    } catch (error) {
      console.error('Failed to confirm delivery:', error);
      showError(error.message || 'Failed to process confirmation. Please try again.');
    } finally {
      setConfirmingDelivery(false);
    }
  };


  const handleCancelOrder = async () => {
    if (!orderToCancel) return;

    try {
      const token = localStorage.getItem('auth_token');
      await cancelOrder(orderToCancel, token);
      showSuccess('Order deleted successfully!');
      fetchOrders();
      if (selectedOrder && selectedOrder.id === orderToCancel) {
        setIsOrderDetailOpen(false);
        setSelectedOrder(null);
        setIsLoadingOrderDetails(false);
      }
    } catch (error) {
      console.error('Failed to delete order:', error);
      showError(error.message || 'Failed to delete order. Please try again.');
    } finally {
      // Always close the modal and reset state
      setIsCancelConfirmOpen(false);
      setOrderToCancel(null);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const updatedUser = await updateProfile(profileForm, token);
      showSuccess('Profile updated successfully!');
      if (onUserUpdate) {
        onUserUpdate(updatedUser.user);
      }
      // Update local storage
      localStorage.setItem('user', JSON.stringify(updatedUser.user));
    } catch (error) {
      console.error('Failed to update profile:', error);
      showError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUseShippingForBilling = (checked) => {
    setUseShippingForBilling(checked);
    if (checked) {
      // Copy shipping info to billing
      setBillingForm({
        billing_address: shippingForm.shipping_address,
        billing_city: shippingForm.shipping_city,
        billing_state: shippingForm.shipping_state,
        billing_postal_code: shippingForm.shipping_postal_code,
        billing_country: shippingForm.shipping_country,
      });
      setUseBillingForShipping(false); // Uncheck the other checkbox
    }
  };

  const handleUseBillingForShipping = (checked) => {
    setUseBillingForShipping(checked);
    if (checked) {
      // Copy billing info to shipping
      setShippingForm({
        shipping_address: billingForm.billing_address,
        shipping_city: billingForm.billing_city,
        shipping_state: billingForm.billing_state,
        shipping_postal_code: billingForm.billing_postal_code,
        shipping_country: billingForm.billing_country,
      });
      setUseShippingForBilling(false); // Uncheck the other checkbox
    }
  };

  const handleShippingChange = (field, value) => {
    const updatedShipping = { ...shippingForm, [field]: value };
    setShippingForm(updatedShipping);
    
    // If "use shipping for billing" is checked, sync to billing
    if (useShippingForBilling) {
      const billingField = field.replace('shipping_', 'billing_');
      setBillingForm({ ...billingForm, [billingField]: value });
    }
  };

  const handleBillingChange = (field, value) => {
    const updatedBilling = { ...billingForm, [field]: value };
    setBillingForm(updatedBilling);
    
    // If "use billing for shipping" is checked, sync to shipping
    if (useBillingForShipping) {
      const shippingField = field.replace('billing_', 'shipping_');
      setShippingForm({ ...shippingForm, [shippingField]: value });
    }
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    setShippingLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const updatedUser = await updateProfile(shippingForm, token);
      showSuccess('Shipping information updated successfully!');
      if (onUserUpdate) {
        onUserUpdate(updatedUser.user);
      }
      localStorage.setItem('user', JSON.stringify(updatedUser.user));
    } catch (error) {
      console.error('Failed to update shipping information:', error);
      showError(error.message || 'Failed to update shipping information. Please try again.');
    } finally {
      setShippingLoading(false);
    }
  };

  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    setBillingLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const updatedUser = await updateProfile(billingForm, token);
      showSuccess('Billing information updated successfully!');
      if (onUserUpdate) {
        onUserUpdate(updatedUser.user);
      }
      localStorage.setItem('user', JSON.stringify(updatedUser.user));
    } catch (error) {
      console.error('Failed to update billing information:', error);
      showError(error.message || 'Failed to update billing information. Please try again.');
    } finally {
      setBillingLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const updatedUser = await updateProfile(paymentForm, token);
      showSuccess('Payment information updated successfully!');
      if (onUserUpdate) {
        onUserUpdate(updatedUser.user);
      }
      localStorage.setItem('user', JSON.stringify(updatedUser.user));
    } catch (error) {
      console.error('Failed to update payment information:', error);
      showError(error.message || 'Failed to update payment information. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please select an image file.');
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showError('Image size must be less than 10MB.');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Upload immediately
      handleProfilePictureUpload(file);
    }
  };

  const handleProfilePictureUpload = async (file) => {
    setProfilePictureLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const updatedUser = await updateProfile({ profile_picture: file }, token);
      showSuccess('Profile picture updated successfully!');
      if (onUserUpdate) {
        onUserUpdate(updatedUser.user);
      }
      localStorage.setItem('user', JSON.stringify(updatedUser.user));
      setProfilePicturePreview(null); // Clear preview after successful upload
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      showError(error.message || 'Failed to upload profile picture. Please try again.');
      setProfilePicturePreview(null);
    } finally {
      setProfilePictureLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.password !== passwordForm.password_confirmation) {
      showError('Passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      await updatePassword({
        current_password: passwordForm.current_password,
        password: passwordForm.password,
        password_confirmation: passwordForm.password_confirmation,
      }, token);
      showSuccess('Password updated successfully!');
      setPasswordForm({
        current_password: '',
        password: '',
        password_confirmation: '',
      });
    } catch (error) {
      console.error('Failed to update password:', error);
      showError(error.message || 'Failed to update password. Please check your current password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      processing: 'bg-blue-100 text-blue-800 border-blue-300',
      shipped: 'bg-purple-100 text-purple-800 border-purple-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      processing: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      shipped: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      ),
      delivered: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      cancelled: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      ),
    };
    return icons[status] || null;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const formatCurrency = (amount) => {
    return `GH¢${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate stats
  const totalOrders = orders.length;
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const deliveredOrders = orders.filter(o => o.status === 'delivered').length;
  // Coupon amount - sum of approved refund amounts
  const couponAmount = orders
    .filter(o => o.status === 'refund_approved' || o.refund_status === 'refund_approved')
    .reduce((sum, order) => sum + parseFloat(order.total || 0), 0);

  return (
    <div className="App">
      <TopPromoBar appName={APP_NAME} />
      <Header 
        logo={logo} 
        appName={APP_NAME}
        cartCount={0}
        onCartClick={() => navigate('/')}
        cartButtonRef={cartButtonRef}
        searchQuery=""
        onSearchChange={() => {}}
        activeFilter={null}
        onFilterChange={() => {}}
        onCategoryFilterChange={() => {}}
        user={user}
        onLoginSuccess={(user) => {
          if (onUserUpdate) {
            onUserUpdate(user);
          }
        }}
        onLogout={onLogout}
      />
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-[85px] sm:pt-[120px] pb-6 sm:pb-12">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-1 h-8 sm:w-1.5 sm:h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full shadow-lg shadow-primary/30"></div>
              <div>
                <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-1 sm:mb-2">My Orders</h1>
                <p className="text-sm sm:text-base text-gray-600 font-medium">View and manage your orders</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 transform"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <span className="hidden sm:inline">Continue Shopping</span>
              <span className="sm:hidden">Shop</span>
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mb-4 sm:mb-6">
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
              <div className="relative bg-gradient-to-br from-blue-50/80 via-white to-white rounded-lg sm:rounded-2xl border-2 border-blue-300/50 p-2 sm:p-4 shadow-lg sm:shadow-xl shadow-blue-200/50 hover:shadow-2xl hover:shadow-blue-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 opacity-70"></div>
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2 gap-1">
                    <span className="text-[8px] sm:text-xs font-bold text-gray-900 uppercase tracking-wider leading-tight">Total Orders</span>
                    <div className="w-6 h-6 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md sm:rounded-lg flex items-center justify-center text-white shadow-lg self-start sm:self-auto">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm sm:text-2xl font-black text-blue-700 leading-tight">{totalOrders}</p>
                </div>
              </div>
              <div className="relative bg-gradient-to-br from-yellow-50/80 via-white to-white rounded-lg sm:rounded-2xl border-2 border-yellow-300/50 p-2 sm:p-4 shadow-lg sm:shadow-xl shadow-yellow-200/50 hover:shadow-2xl hover:shadow-yellow-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-yellow-500 to-yellow-600 opacity-70"></div>
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2 gap-1">
                    <span className="text-[8px] sm:text-xs font-bold text-gray-900 uppercase tracking-wider leading-tight">Pending</span>
                    <div className="w-6 h-6 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md sm:rounded-lg flex items-center justify-center text-white shadow-lg self-start sm:self-auto">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm sm:text-2xl font-black text-yellow-700 leading-tight">{pendingOrders}</p>
                </div>
              </div>
              <div className="relative bg-gradient-to-br from-green-50/80 via-white to-white rounded-lg sm:rounded-2xl border-2 border-green-300/50 p-2 sm:p-4 shadow-lg sm:shadow-xl shadow-green-200/50 hover:shadow-2xl hover:shadow-green-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-green-600 opacity-70"></div>
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2 gap-1">
                    <span className="text-[8px] sm:text-xs font-bold text-gray-900 uppercase tracking-wider leading-tight">Delivered</span>
                    <div className="w-6 h-6 sm:w-10 sm:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-md sm:rounded-lg flex items-center justify-center text-white shadow-lg self-start sm:self-auto">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm sm:text-2xl font-black text-green-700 leading-tight">{deliveredOrders}</p>
                </div>
              </div>
              <div className="relative bg-gradient-to-br from-purple-50/80 via-white to-white rounded-lg sm:rounded-2xl border-2 border-purple-300/50 p-2 sm:p-4 shadow-lg sm:shadow-xl shadow-purple-200/50 hover:shadow-2xl hover:shadow-purple-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-purple-600 opacity-70"></div>
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2 gap-1">
                    <span className="text-[8px] sm:text-xs font-bold text-gray-900 uppercase tracking-wider leading-tight">Coupon Amount</span>
                    <div className="w-6 h-6 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md sm:rounded-lg flex items-center justify-center text-white shadow-lg self-start sm:self-auto">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm sm:text-2xl font-black text-purple-700 leading-tight">{formatCurrency(couponAmount)}</p>
                </div>
              </div>
            </div>
          </div>

        {/* Main Card */}
        <div className="relative bg-gradient-to-br from-white via-gray-50/30 to-white rounded-2xl border-2 border-gray-300/50 shadow-xl shadow-gray-300/50 overflow-hidden">
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-dark to-secondary opacity-60"></div>
          
          {/* Orders Content */}
          <div className="p-4 sm:p-6">
              {/* Status Filter */}
              <div className="mb-4 sm:mb-6">
                <div className="sm:flex sm:flex-wrap gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                  <div className="flex gap-2 sm:flex-wrap min-w-max sm:min-w-0">
                    {['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => (
                      <button
                        key={status}
                        onClick={() => setStatusFilter(status)}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                          statusFilter === status
                            ? 'bg-gradient-to-r from-primary via-primary to-primary-dark text-white shadow-lg shadow-primary/30'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                        }`}
                      >
                        {status === 'all' ? 'All Orders' : getStatusLabel(status)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Orders List */}
              {loading ? (
                <div className="text-center py-16">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  <p className="text-gray-600 mt-4 font-medium">Loading orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-20">
                  <div className="relative w-32 h-32 mx-auto mb-6">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl blur-xl opacity-50"></div>
                    <div className="relative w-32 h-32 bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl flex items-center justify-center border-2 border-gray-200/60 shadow-lg">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-700 font-bold text-xl mb-2">No orders found</p>
                  <p className="text-gray-500 text-sm mb-6">
                    {statusFilter === 'all' 
                      ? "You haven't placed any orders yet."
                      : `No ${getStatusLabel(statusFilter).toLowerCase()} orders found.`}
                  </p>
                  <button
                    onClick={() => navigate('/')}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-primary/30 hover:-translate-y-1 transition-all duration-300 transform"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    Continue Shopping
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="group relative bg-gradient-to-br from-white via-gray-50/50 to-white rounded-lg sm:rounded-xl border-2 border-gray-200/60 p-4 sm:p-6 hover:shadow-xl hover:shadow-gray-300/50 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                    >
                      {/* Decorative gradient */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      <div className="relative flex flex-col gap-3 sm:gap-4">
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-2 mb-3">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Order #{order.order_number}</h3>
                            <div className="flex items-center gap-2">
                              {order.payment_status === 'pending_payment' && (
                                <AwaitingPaymentBanner order={order} />
                              )}
                              {(order.payment_status === 'failed' || order.payment_status === 'Failed') && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-bold border-2 border-red-500 bg-red-50 text-red-700 w-fit">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  Payment Failed
                                </span>
                              )}
                              {order.payment_status === 'paid' && order.status === 'pending' && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-bold border-2 border-green-500 bg-green-50 text-green-700 w-fit">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Payment Confirmed
                                </span>
                              )}
                              <span className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-bold border w-fit ${getStatusColor(order.status)}`}>
                                {getStatusIcon(order.status)}
                                {getStatusLabel(order.status)}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-1.5 sm:space-y-1">
                            <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {formatDate(order.created_at)}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                              </svg>
                              {(order.orderItems || order.order_items)?.length || 0} item(s) • Total: <span className="font-bold text-primary text-sm sm:text-base">{formatCurrency(order.total)}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
                          {(order.payment_status === 'failed' || order.payment_status === 'Failed') ? (
                            <button
                              onClick={() => handleRetryPayment(order)}
                              className="flex-1 sm:flex-none px-4 sm:px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-green-300/30 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Retry Payment
                            </button>
                          ) : (
                            <button
                              onClick={() => handleViewOrder(order.id)}
                              className="flex-1 sm:flex-none px-4 sm:px-5 py-2.5 bg-gradient-to-r from-primary via-primary to-primary-dark text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View Details
                            </button>
                          )}
                          {order.status === 'pending' && (
                            <button
                              onClick={() => handleCancelOrderClick(order.id)}
                              className="flex-1 sm:flex-none px-4 sm:px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-red-300/30 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Account Tab Content - Moved to Profile page */}
          {false && (
            <div className="p-4 sm:p-6">
              <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
                {/* Right Column - Profile Sidebar - Show First on Mobile */}
                <div className="lg:w-80 flex-shrink-0 order-1 lg:order-2">
                  <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-lg sm:rounded-xl border-2 border-gray-200/50 p-3 sm:p-6 shadow-lg lg:sticky lg:top-24">
                    {/* Profile Picture */}
                    <div className="flex flex-col items-center mb-3 sm:mb-6">
                      <div className="relative mb-2 sm:mb-4">
                        <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-primary via-primary-dark to-secondary flex items-center justify-center text-white text-xl sm:text-4xl font-bold shadow-xl border-3 sm:border-4 border-white overflow-hidden">
                          {profilePicturePreview || user?.profile_picture ? (
                            <img 
                              src={profilePicturePreview || user.profile_picture} 
                              alt={user?.name || 'Profile'} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span>{user?.name ? user.name.charAt(0).toUpperCase() : 'U'}</span>
                          )}
                          {profilePictureLoading && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <svg className="animate-spin h-6 w-6 sm:h-8 sm:w-8 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                          )}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                          className="hidden"
                          id="profile-picture-input"
                        />
                        <label
                          htmlFor="profile-picture-input"
                          className="absolute bottom-0 right-0 w-7 h-7 sm:w-10 sm:h-10 bg-primary rounded-full flex items-center justify-center text-white shadow-lg hover:bg-primary-dark transition-colors border-2 border-white cursor-pointer"
                          title="Change profile picture"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </label>
                      </div>
                      <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-0.5 sm:mb-1">{user?.name || 'User'}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm break-all">{user?.email || ''}</p>
                    </div>

                    {/* User Details */}
                    <div className="space-y-2.5 sm:space-y-4 border-t border-gray-200 pt-3 sm:pt-6">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-7 h-7 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5 sm:mb-1">ROLE</p>
                          <p className="text-xs sm:text-base font-bold text-gray-900 break-words">{user?.role?.name || 'Customer'}</p>
                        </div>
                      </div>
                      {user?.phone && (
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-7 h-7 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5 sm:mb-1">PHONE</p>
                            <p className="text-xs sm:text-base font-bold text-gray-900 break-words">{user.phone}</p>
                          </div>
                        </div>
                      )}
                      {user?.created_at && (
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-7 h-7 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5 sm:mb-1">MEMBER SINCE</p>
                            <p className="text-xs sm:text-base font-bold text-gray-900 break-words">{formatDate(user.created_at)}</p>
                          </div>
                        </div>
                      )}
                      {user?.address && (
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="w-7 h-7 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5 sm:mb-1">ADDRESS</p>
                            <p className="text-xs sm:text-base font-bold text-gray-900 break-words">{user.address}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Account Summary */}
                    <div className="mt-3 sm:mt-6 pt-3 sm:pt-6 border-t border-gray-200">
                      <h4 className="text-sm sm:text-lg font-bold text-gray-900 mb-2 sm:mb-4">ACCOUNT SUMMARY</h4>
                      <div className="grid grid-cols-2 gap-2 sm:gap-4">
                        <div className="bg-blue-50 rounded-lg sm:rounded-xl p-2.5 sm:p-4 border border-blue-200">
                          <p className="text-[9px] sm:text-xs font-semibold text-gray-600 mb-1 sm:mb-2">Total Orders</p>
                          <p className="text-lg sm:text-2xl font-black text-blue-700">{totalOrders}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg sm:rounded-xl p-2.5 sm:p-4 border border-green-200">
                          <p className="text-[9px] sm:text-xs font-semibold text-gray-600 mb-1 sm:mb-2">Coupon Amount</p>
                          <p className="text-lg sm:text-2xl font-black text-green-700">{formatCurrency(couponAmount)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Left Column - Forms */}
                <div className="flex-1 space-y-4 sm:space-y-8 order-2 lg:order-1">
                {/* Profile Section */}
                <div className="bg-gradient-to-br from-blue-50/30 via-white to-white rounded-lg sm:rounded-xl border-2 border-blue-200/50 p-4 sm:p-6 shadow-lg">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center text-white shadow-lg">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Profile Information</h2>
                  </div>
                  <form onSubmit={handleProfileSubmit} className="space-y-4 sm:space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setProfileForm({ ...profileForm, phone: digitsOnly });
                        }}
                        maxLength={10}
                        inputMode="numeric"
                        autoComplete="tel"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Address
                      </label>
                      <textarea
                        value={profileForm.address}
                        onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none bg-white text-sm sm:text-base"
                        rows={3}
                        placeholder="Optional"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={profileLoading}
                      className="w-full px-4 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-primary via-primary to-primary-dark text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {profileLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      ) : 'Save Changes'}
                    </button>
                  </form>
                </div>

                {/* Password Section */}
                <div className="bg-gradient-to-br from-purple-50/30 via-white to-white rounded-lg sm:rounded-xl border-2 border-purple-200/50 p-4 sm:p-6 shadow-lg">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Change Password</h2>
                  </div>
                  <form onSubmit={handlePasswordSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                        required
                        minLength={8}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={passwordForm.password_confirmation}
                        onChange={(e) => setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                        required
                        minLength={8}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={passwordLoading}
                      className="w-full px-4 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-primary via-primary to-primary-dark text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {passwordLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating...
                        </span>
                      ) : 'Update Password'}
                    </button>
                  </form>
                </div>

                {/* Shipping Information Section */}
                <div className="bg-gradient-to-br from-green-50/30 via-white to-white rounded-lg sm:rounded-xl border-2 border-green-200/50 p-4 sm:p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                      <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Shipping Information</h2>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useBillingForShipping}
                        onChange={(e) => handleUseBillingForShipping(e.target.checked)}
                        className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2"
                      />
                      <span className="text-sm font-semibold text-gray-700">Use Billing Information</span>
                    </label>
                  </div>
                  <form onSubmit={handleShippingSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Shipping Address
                      </label>
                      <textarea
                        value={shippingForm.shipping_address}
                        onChange={(e) => handleShippingChange('shipping_address', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none bg-white text-sm sm:text-base"
                        rows={3}
                        placeholder="Street address, apartment, suite, etc."
                        disabled={useBillingForShipping}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          value={shippingForm.shipping_city}
                          onChange={(e) => handleShippingChange('shipping_city', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                          placeholder="City"
                          disabled={useBillingForShipping}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          State/Province
                        </label>
                        <input
                          type="text"
                          value={shippingForm.shipping_state}
                          onChange={(e) => handleShippingChange('shipping_state', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                          placeholder="State/Province"
                          disabled={useBillingForShipping}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          value={shippingForm.shipping_postal_code}
                          onChange={(e) => handleShippingChange('shipping_postal_code', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                          placeholder="Postal Code"
                          disabled={useBillingForShipping}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Country
                        </label>
                        <input
                          type="text"
                          value={shippingForm.shipping_country}
                          onChange={(e) => handleShippingChange('shipping_country', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                          placeholder="Country"
                          disabled={useBillingForShipping}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={shippingLoading}
                      className="w-full px-4 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-green-500 via-green-600 to-green-700 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-green-300/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {shippingLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      ) : 'Save Shipping Information'}
                    </button>
                  </form>
                </div>

                {/* Billing Information Section */}
                <div className="bg-gradient-to-br from-orange-50/30 via-white to-white rounded-lg sm:rounded-xl border-2 border-orange-200/50 p-4 sm:p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Billing Information</h2>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useShippingForBilling}
                        onChange={(e) => handleUseShippingForBilling(e.target.checked)}
                        className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2"
                      />
                      <span className="text-sm font-semibold text-gray-700">Use Shipping Information</span>
                    </label>
                  </div>
                  <form onSubmit={handleBillingSubmit} className="space-y-4 sm:space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Billing Address
                      </label>
                      <textarea
                        value={billingForm.billing_address}
                        onChange={(e) => handleBillingChange('billing_address', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                        rows={3}
                        placeholder="Street address, apartment, suite, etc."
                        disabled={useShippingForBilling}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          value={billingForm.billing_city}
                          onChange={(e) => handleBillingChange('billing_city', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                          placeholder="City"
                          disabled={useShippingForBilling}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          State/Province
                        </label>
                        <input
                          type="text"
                          value={billingForm.billing_state}
                          onChange={(e) => handleBillingChange('billing_state', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                          placeholder="State/Province"
                          disabled={useShippingForBilling}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          value={billingForm.billing_postal_code}
                          onChange={(e) => handleBillingChange('billing_postal_code', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                          placeholder="Postal Code"
                          disabled={useShippingForBilling}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Country
                        </label>
                        <input
                          type="text"
                          value={billingForm.billing_country}
                          onChange={(e) => handleBillingChange('billing_country', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
                          placeholder="Country"
                          disabled={useShippingForBilling}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={billingLoading}
                      className="w-full px-4 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-orange-300/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {billingLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      ) : 'Save Billing Information'}
                    </button>
                  </form>
                </div>

                {/* Payment Information Section */}
                <div className="bg-gradient-to-br from-indigo-50/30 via-white to-white rounded-xl border-2 border-indigo-200/50 p-6 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Payment Information</h2>
                  </div>
                  <form onSubmit={handlePaymentSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Preferred Payment Method
                      </label>
                      <select
                        value={paymentForm.payment_method}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                      >
                        <option value="">Select payment method</option>
                        <option value="card">Credit/Debit Card</option>
                        <option value="mobile_money">Mobile Money</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="cash_on_delivery">Cash on Delivery</option>
                      </select>
                    </div>
                    {paymentForm.payment_method === 'card' && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">
                              Card Brand
                            </label>
                            <select
                              value={paymentForm.card_brand}
                              onChange={(e) => setPaymentForm({ ...paymentForm, card_brand: e.target.value })}
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                            >
                              <option value="">Select card brand</option>
                              <option value="visa">Visa</option>
                              <option value="mastercard">Mastercard</option>
                              <option value="amex">American Express</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">
                              Last 4 Digits
                            </label>
                            <input
                              type="text"
                              value={paymentForm.card_last_four}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                                setPaymentForm({ ...paymentForm, card_last_four: value });
                              }}
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-white text-sm sm:text-base"
                              placeholder="1234"
                              maxLength={4}
                            />
                            <p className="text-xs text-gray-500 mt-1">Last 4 digits of your card (for reference only)</p>
                          </div>
                        </div>
                      </>
                    )}
                    <button
                      type="submit"
                      disabled={paymentLoading}
                      className="w-full px-4 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-indigo-500 via-indigo-600 to-indigo-700 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-indigo-300/30 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {paymentLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      ) : 'Save Payment Information'}
                    </button>
                  </form>
                </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Modal */}
      {isOrderDetailOpen && selectedOrder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-1 sm:p-2 md:p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl max-w-4xl w-full my-2 sm:my-4 max-h-[95vh] overflow-y-auto animate-modal-pop">
              <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 flex items-center justify-between sticky top-0 z-10 shadow-lg">
                {isLoadingOrderDetails && (
                  <div className="absolute top-1.5 right-12 sm:right-14 flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
                    <svg className="animate-spin h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-[10px] text-white font-medium">Loading...</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg md:text-xl font-bold text-white">Order Details</h2>
                    <p className="text-[10px] sm:text-xs text-white/80">{selectedOrder.order_number}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsOrderDetailOpen(false);
                    setSelectedOrder(null);
                    setIsLoadingOrderDetails(false);
                  }}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-3 sm:p-4 md:p-5 space-y-2.5 sm:space-y-3 md:space-y-4">
                {/* Customer & Order Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3">
                  {/* Customer Information */}
                  <div className="bg-gradient-to-br from-blue-50/50 to-white rounded-lg border border-blue-200/50 p-2.5 sm:p-3">
                    <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Customer Information
                    </h3>
                    <div className="space-y-1 text-[11px] sm:text-sm">
                      <p><span className="font-semibold text-gray-600">Name:</span> <span className="text-gray-900">{selectedOrder.user?.name || 'N/A'}</span></p>
                      <p><span className="font-semibold text-gray-600">Email:</span> <span className="text-gray-900 break-all">{selectedOrder.user?.email || 'N/A'}</span></p>
                      <p><span className="font-semibold text-gray-600">Phone:</span> <span className="text-gray-900">{selectedOrder.phone || selectedOrder.user?.phone || 'N/A'}</span></p>
                    </div>
                  </div>
                  
                  {/* Order Information */}
                  <div className="bg-gradient-to-br from-purple-50/50 to-white rounded-lg border border-purple-200/50 p-2.5 sm:p-3">
                    <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Order Information
                    </h3>
                    <div className="space-y-1.5 text-[11px] sm:text-sm">
                      <p><span className="font-semibold text-gray-600">Date:</span> <span className="text-gray-900">{formatDate(selectedOrder.created_at)}</span></p>
                      {selectedOrder.tracking_number && (
                        <p className="flex items-center gap-1.5">
                          <span className="font-semibold text-gray-600">Tracking #:</span>
                          <span className="text-emerald-700 font-mono font-bold">{selectedOrder.tracking_number}</span>
                          <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </p>
                      )}
                      {selectedOrder.delivery_otp && (
                        <p><span className="font-semibold text-gray-600">Delivery OTP:</span> <span className="text-blue-700 font-mono font-bold text-sm sm:text-base">{selectedOrder.delivery_otp}</span></p>
                      )}
                      <p><span className="font-semibold text-gray-600">Total:</span> <span className="text-primary font-bold text-sm sm:text-base">{formatCurrency(selectedOrder.total)}</span></p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusColor(selectedOrder.status)}`}>
                          {getStatusIcon(selectedOrder.status)}
                          {getStatusLabel(selectedOrder.status)}
                        </span>
                        {selectedOrder.payment_status === 'pending_payment' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-amber-400 bg-amber-50 text-amber-800">Awaiting payment</span>
                        )}
                        {(selectedOrder.payment_status === 'failed' || selectedOrder.payment_status === 'Failed') && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-red-400 bg-red-50 text-red-800">Payment failed</span>
                        )}
                        {selectedOrder.payment_status === 'paid' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-green-500 bg-green-50 text-green-700">Payment successful</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="bg-gradient-to-br from-gray-50/50 to-white rounded-lg border border-gray-200/50 p-2.5 sm:p-3">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    Order Items
                  </h3>
                  <div className="space-y-2">
                    {(selectedOrder.orderItems || selectedOrder.order_items) && (selectedOrder.orderItems || selectedOrder.order_items).length > 0 ? (
                      (selectedOrder.orderItems || selectedOrder.order_items).map((item) => (
                        <div key={item.id} className="flex gap-2 p-2 bg-white rounded-lg border border-gray-200/50 hover:shadow-sm transition-shadow">
                          {item.product?.images?.[0] || item.product?.image ? (
                            <img
                              src={item.product.images?.[0] || item.product.image}
                              alt={item.product?.name || 'Product'}
                              className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0">
                              <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <h4 className="font-bold text-gray-900 text-xs sm:text-sm mb-1 line-clamp-2 leading-tight">{item.product?.name || `Product #${item.product_id || item.id}`}</h4>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] sm:text-xs">
                              <span className="text-gray-600">Qty: <span className="font-semibold">{item.quantity}</span></span>
                              <span className="text-gray-400">×</span>
                              <span className="text-gray-600 font-semibold">{formatCurrency(item.price || item.unit_price || 0)}</span>
                              <span className="text-primary font-bold ml-auto">Subtotal: {formatCurrency(item.subtotal || (item.price || item.unit_price || 0) * item.quantity)}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-6">
                        <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        <p className="text-sm text-gray-500 font-medium">No items found</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-gradient-to-br from-blue-50/50 to-white rounded-lg border border-blue-200/50 p-2.5 sm:p-3">
                  <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-1.5 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Shipping Address
                  </h3>
                  <p className="text-[11px] sm:text-sm text-gray-700 whitespace-pre-line leading-relaxed">{selectedOrder.shipping_address}</p>
                </div>

                {/* Notes */}
                {selectedOrder.notes && (
                  <div className="bg-gradient-to-br from-amber-50/50 to-white rounded-lg border border-amber-200/50 p-2.5 sm:p-3">
                    <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-1.5 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Order Notes
                    </h3>
                    <p className="text-[11px] sm:text-sm text-gray-700 whitespace-pre-line leading-relaxed">{selectedOrder.notes}</p>
                  </div>
                )}

                {/* Delivery Proof Section - Only show for delivered orders */}
                {selectedOrder.status === 'delivered' && (
                  <div className="bg-gradient-to-br from-green-50/50 to-white rounded-lg border border-green-200/50 p-2.5 sm:p-3">
                    <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Delivery Proof
                    </h3>
                    
                    {selectedOrder.delivery_proof_image && (
                      <div className="mb-2">
                        <img
                          src={selectedOrder.delivery_proof_image}
                          alt="Delivery proof"
                          className="w-full max-w-xs rounded-lg border border-gray-200"
                        />
                      </div>
                    )}
                    
                    {selectedOrder.delivery_notes && (
                      <div className="mb-2">
                        <p className="text-[11px] sm:text-sm text-gray-700 whitespace-pre-line leading-relaxed">{selectedOrder.delivery_notes}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] sm:text-sm">
                      {selectedOrder.delivered_by && (
                        <p><span className="font-semibold text-gray-600">Delivered By:</span> <span className="text-gray-700">{selectedOrder.delivered_by}</span></p>
                      )}
                      {selectedOrder.delivery_location && (
                        <p><span className="font-semibold text-gray-600">Location:</span> <span className="text-gray-700">{selectedOrder.delivery_location}</span></p>
                      )}
                      {selectedOrder.delivered_at && (
                        <p className="sm:col-span-2"><span className="font-semibold text-gray-600">Delivered At:</span> <span className="text-gray-700">{formatDate(selectedOrder.delivered_at)}</span></p>
                      )}
                    </div>

                    {/* Customer Confirmation Status */}
                    {selectedOrder.customer_confirmation && (
                      <div className={`mt-2 p-2.5 rounded-lg border ${
                        selectedOrder.customer_confirmation === 'confirmed' 
                          ? 'bg-green-50 border-green-300' 
                          : selectedOrder.customer_confirmation === 'disputed'
                          ? 'bg-red-50 border-red-300'
                          : 'bg-yellow-50 border-yellow-300'
                      }`}>
                        <div className="flex items-center gap-1.5">
                          {selectedOrder.customer_confirmation === 'confirmed' && (
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          {selectedOrder.customer_confirmation === 'disputed' && (
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          )}
                          <p className={`text-xs sm:text-sm font-bold ${
                            selectedOrder.customer_confirmation === 'confirmed' 
                              ? 'text-green-800' 
                              : selectedOrder.customer_confirmation === 'disputed'
                              ? 'text-red-800'
                              : 'text-yellow-800'
                          }`}>
                            {selectedOrder.customer_confirmation === 'confirmed' 
                              ? 'Confirmed' 
                              : selectedOrder.customer_confirmation === 'disputed'
                              ? 'Disputed'
                              : 'Pending Confirmation'}
                          </p>
                        </div>
                        {selectedOrder.customer_dispute_reason && (
                          <p className="text-[11px] text-gray-700 mt-1.5">Reason: {selectedOrder.customer_dispute_reason}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2.5 border-t border-gray-200">
                  {selectedOrder.status === 'pending' && (
                    <button
                      onClick={() => handleCancelOrderClick(selectedOrder.id)}
                      className="px-4 py-2 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Cancel Order
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setIsOrderDetailOpen(false);
                      setSelectedOrder(null);
                      setIsLoadingOrderDetails(false);
                    }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:shadow-md transition-all duration-200 flex items-center justify-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dispute Delivery Modal */}
        {showDisputeModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-modal-pop">
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5 rounded-t-2xl">
                <h2 className="text-2xl font-bold text-white">Dispute Delivery</h2>
              </div>
              
              <div className="p-6">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Didn't Receive Your Order?</h3>
                  <p className="text-gray-600 text-center mb-4">
                    Please provide details about why you're disputing this delivery. Admin will review your claim.
                  </p>
                  <textarea
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    placeholder="Explain why you didn't receive the order..."
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all resize-none"
                    rows="4"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDisputeModal(false);
                      setDisputeReason('');
                    }}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleConfirmDelivery('dispute')}
                    disabled={confirmingDelivery || !disputeReason.trim()}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:shadow-xl hover:shadow-red-500/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {confirmingDelivery ? 'Submitting...' : 'Submit Dispute'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Cancel / Delete Order Confirmation Modal */}
      {isCancelConfirmOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-modal-pop">
              <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-6 py-5 rounded-t-2xl">
                <h2 className="text-2xl font-bold text-white">Cancel Order</h2>
              </div>
              
              <div className="p-6">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Are you sure?</h3>
                  <p className="text-gray-600 text-center">
                    This action cannot be undone. The order will be deleted permanently and you may need to place a new order.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsCancelConfirmOpen(false);
                      setOrderToCancel(null);
                    }}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                  >
                    Keep Order
                  </button>
                  <button
                    onClick={handleCancelOrder}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-xl font-semibold hover:shadow-xl hover:shadow-primary/40 transition-all duration-200"
                  >
                    Cancel Order
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
  </div>
  );
}

export default UserOrders;
